import React, { useState, useEffect, useRef, useMemo } from "react";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import { SearchBar } from "components/common/SearchBar";
import { EmailSection } from "./EmailSection";
import { AccessList } from "./AccessList";
import {
  EntityType,
  RefetchOptions,
  RemoveAccessData,
  ResourceType,
  Permission,
  ShareOutcome,
} from "components/shareSection/entities";
import { SharedAccess, UserModelRole } from "target_wasm";
import { InlineSpinner } from "components/common/InlineSpinner";
import { LoadMoreButton } from "components/common/LoadMoreButton";
interface UserAccessPanelProps {
  resourceType: ResourceType;
  entityId: string;
  sharedWithList: SharedAccess | undefined;
  setSharedWithList?: React.Dispatch<
    React.SetStateAction<SharedAccess | undefined>
  >;
  canShare: boolean;
  shareDisabled?: boolean;
  canRevokeAccess: boolean;
  defaultRole?: UserModelRole;
  defaultPermission?: Permission;
  continuationToken?: string | null;
  onRefetchSharedList?: (options?: RefetchOptions) => Promise<void>;
  onShare: (
    emails: string[],
    roleOrPermission: UserModelRole | Permission | null,
  ) => Promise<ShareOutcome | void>;
  onRevokeAccess: (data: RemoveAccessData) => Promise<void>;
  setIsShare: React.Dispatch<React.SetStateAction<boolean>> | (() => void);
  modelName?: string;
  onResendInvitation?: (email: string) => Promise<void>;
}

export const UserAccessPanel: React.FC<UserAccessPanelProps> = (props) => {
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isInitialMountRef = useRef(true);
  const hasSearchedViaApi = useRef(false);
  const searchBarShown = useRef(false);

  const hasMore = !!props.continuationToken;

  const userAccessList = useMemo(() => {
    const allUsers = props.sharedWithList?.users() || [];
    return props.resourceType === ResourceType.Table
      ? allUsers.filter((access) => access.resource_js() === ResourceType.Table)
      : allUsers;
  }, [props.sharedWithList, props.resourceType]);

  searchBarShown.current = userAccessList.length > 0 || !!userSearchQuery;
  useEffect(() => {
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    if (!userSearchQuery) {
      setIsSearching(false);
      if (hasSearchedViaApi.current && props.onRefetchSharedList) {
        props.onRefetchSharedList({ entityType: "users" });
        hasSearchedViaApi.current = false;
      }
      return;
    }

    if (!hasMore && !hasSearchedViaApi.current) return;

    if (props.onRefetchSharedList) {
      setIsSearching(true);
      searchTimeoutRef.current = setTimeout(() => {
        props
          .onRefetchSharedList?.({
            search: userSearchQuery,
            entityType: "users",
          })
          .then(() => {
            setIsSearching(false);
          });
        hasSearchedViaApi.current = true;
      }, 300);
    }

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [userSearchQuery]);

  const filteredUserAccessList = useMemo(() => {
    if (!userSearchQuery) return userAccessList;
    const term = userSearchQuery.toLowerCase();
    return userAccessList.filter(
      (access) =>
        access.user_js().user_email_js().toLowerCase().includes(term) ||
        access.user_js().user_name_js().toLowerCase().includes(term),
    );
  }, [userAccessList, userSearchQuery]);

  const handleLoadMore = async () => {
    if (!props.continuationToken || !props.onRefetchSharedList) return;
    setIsLoadingMore(true);
    try {
      await props.onRefetchSharedList({
        continuationToken: props.continuationToken,
        entityType: "users",
        ...(userSearchQuery ? { search: userSearchQuery } : {}),
      });
    } finally {
      setIsLoadingMore(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div
        className="transition-all duration-400 ease-in-out max-h-96 opacity-100 transform translate-y-0"
        style={{ overflow: "visible" }}
      >
        {props.canShare && (
          <EmailSection
            entityType={EntityType.User}
            entityId={props.entityId}
            shareAction={props.onShare}
            resourceType={props.resourceType}
            defaultRole={props.defaultRole ?? null}
            defaultPermission={props.defaultPermission ?? null}
            setIsShare={props.setIsShare}
            sharedWith={props.sharedWithList}
            canRevokeAccess={props.canRevokeAccess}
            shareDisabled={props.shareDisabled}
          />
        )}
      </div>

      <div>
        <div className="flex items-center gap-1 h-8 mb-2 cursor-default">
          <PeopleOutlinedIcon className="text-fg-muted-medium icon-sm" />
          <p className="text-fg-muted-semibold">
            People with access
            {filteredUserAccessList.length === 0 && !userSearchQuery
              ? " (0)"
              : ""}
          </p>
        </div>
        {searchBarShown.current && (
          <div className="mb-2">
            <SearchBar
              value={userSearchQuery}
              onChange={setUserSearchQuery}
              placeholder="Search users"
              onClose={() => setUserSearchQuery("")}
              actionTooltip="Clear Search"
            />
          </div>
        )}
        <div className="relative max-h-52 overflow-y-auto">
          {filteredUserAccessList.length === 0 ? (
            isSearching ? (
              <div className="flex justify-center py-4">
                <InlineSpinner size={20} />
              </div>
            ) : userSearchQuery ? (
              <p className="text-fg-muted-medium text-sm py-2">
                No users found
              </p>
            ) : null
          ) : (
            <>
              <AccessList
                entityId={props.entityId}
                sharedWith={filteredUserAccessList}
                resourceType={props.resourceType}
                canRevokeAccess={props.canRevokeAccess}
                onRevokeAccess={props.onRevokeAccess}
                setSharedWithList={props.setSharedWithList}
                modelName={props.modelName}
                onResendInvitation={props.onResendInvitation}
              />
              {hasMore && (
                <LoadMoreButton
                  onClick={handleLoadMore}
                  isLoading={isLoadingMore}
                />
              )}
            </>
          )}
          {isSearching && filteredUserAccessList.length > 0 && (
            <div className="absolute inset-0 bg-bg/60 rounded pointer-events-none" />
          )}
        </div>
      </div>
    </div>
  );
};
